import { useEffect, useState, useCallback } from "react";
import { Bot, Loader2, RefreshCw, Clock, PhoneCall, FileText, Copy, Play, Settings2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import CampaignsPanel from "./CampaignsPanel";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`);
const fmtAt = (d) => (d ? new Date(d).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Brussels" }) : "");

function Transcript({ callId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/vapi/calls/${callId}/transcript`).then((r) => setData(r.data)).catch(() => setData({ messages: [], transcript: "" })); }, [callId]);
  if (!data) return <Loader2 className="w-4 h-4 animate-spin text-[#4F46E5]" />;
  if (!data.messages.length) return <pre data-testid={`nicole-transcript-raw-${callId}`} className="text-xs whitespace-pre-wrap text-[#3B423E] max-h-72 overflow-auto">{data.transcript || "Transcription indisponible."}</pre>;
  return (
    <div data-testid={`nicole-transcript-${callId}`} className="space-y-1.5 max-h-80 overflow-auto bg-[#F8FAFF] rounded-lg p-2">
      {data.messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "nicole" ? "justify-start" : "justify-end"}`}>
          <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs ${m.role === "nicole" ? "bg-[#4F46E5] text-white rounded-bl-sm" : "bg-white border border-[#E2DFD8] text-[#1A1A1A] rounded-br-sm"}`}>
            <div className="text-[10px] font-bold opacity-70">{m.role === "nicole" ? "Sara Dupont" : "Restaurant"}{m.t != null ? ` · ${Math.floor(m.t / 60)}:${String(Math.round(m.t % 60)).padStart(2, "0")}` : ""}</div>
            {m.text}
          </div>
        </div>
      ))}
    </div>
  );
}

function Recording({ callId }) {
  const [state, setState] = useState({ url: null, loading: false, error: null });
  const load = async () => {
    setState({ url: null, loading: true, error: null });
    try {
      const { data } = await api.get(`/admin/vapi/calls/${callId}/recording`);
      setState({ url: data.url, loading: false, error: null });
    } catch (e) {
      setState({ url: null, loading: false, error: e?.response?.data?.detail || "Enregistrement indisponible." });
    }
  };
  if (state.url) return <audio data-testid={`nicole-call-audio-${callId}`} controls autoPlay src={state.url} onError={load} className="w-full sm:flex-1 h-9" />;
  return (
    <div className="flex items-center gap-2 sm:flex-1">
      <button data-testid={`nicole-call-play-${callId}`} onClick={load} disabled={state.loading}
        className="h-8 px-3 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 transition-colors">
        {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Écouter l'enregistrement
      </button>
      {state.error && <span data-testid={`nicole-call-audio-error-${callId}`} className="text-xs text-[#991b1b]">{state.error}</span>}
    </div>
  );
}

export function NicoleCallRow({ c, showName = false, transcriptOpen = false, onDeleted }) {
  const [open, setOpen] = useState(transcriptOpen);
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cet appel (enregistrement et transcription) ?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/vapi/calls/${c.id}`);
      toast.success("Appel supprimé");
      onDeleted?.(c.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); setDeleting(false); }
  };
  return (
    <div data-testid={`nicole-call-${c.id}`} className="rounded-xl border border-[#E0E7FF] bg-white p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <button data-testid={`nicole-call-delete-${c.id}`} onClick={remove} disabled={deleting} title="Supprimer cet appel"
          className="h-7 w-7 rounded-lg bg-[#FEE2E2] hover:bg-[#FECACA] text-[#991b1b] inline-flex items-center justify-center disabled:opacity-60 transition-colors">
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
        <span className="inline-flex items-center gap-1 font-bold text-[#4F46E5]"><Bot className="w-3.5 h-3.5" /> {showName ? c.prospect_name : "Sara Dupont"}</span>
        <span className="text-[#75807A]">{fmtAt(c.created_at)}</span>
        <span className="inline-flex items-center gap-1 text-[#3B423E]" data-testid={`nicole-call-duration-${c.id}`}><Clock className="w-3 h-3" /> {fmtDur(c.duration_seconds)}</span>
        <span className="px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4338CA] font-semibold">{c.origin || "manuel"}</span>
        <span className={`px-2 py-0.5 rounded-full font-semibold ${c.status === "ended" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FFF3E6] text-[#7A3A00]"}`}>{c.status === "ended" ? "Terminé" : "En cours"}</span>
      </div>
      {c.summary && <p className="text-xs text-[#3B423E]" data-testid={`nicole-call-summary-${c.id}`}>{c.summary}</p>}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        {c.status === "ended" && <Recording callId={c.id} />}
        {c.status === "ended" && (
          <button data-testid={`nicole-transcript-toggle-${c.id}`} onClick={() => setOpen((o) => !o)}
            className="h-8 px-3 rounded-lg bg-[#EEF2FF] hover:bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold inline-flex items-center gap-1 shrink-0 transition-colors">
            <FileText className="w-3.5 h-3.5" /> {open ? "Masquer" : "Transcription"}
          </button>
        )}
      </div>
      {open && <Transcript callId={c.id} />}
    </div>
  );
}

export function ProspectNicoleCalls({ prospectId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/vapi/calls", { params: { prospect_id: prospectId, limit: 10 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [prospectId]);
  if (!items?.length) return null;
  return (
    <div className="pt-2 border-t border-[#F0EBE1] space-y-2" data-testid={`prosp-nicole-journal-${prospectId}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#4F46E5]">Appels Sara Dupont</div>
      {items.map((c) => <NicoleCallRow key={c.id} c={c} transcriptOpen onDeleted={(id) => setItems((l) => l.filter((x) => x.id !== id))} />)}
    </div>
  );
}

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function NicoleSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/vapi/settings").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const toggle = (d) => setCfg((c) => ({ ...c, days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d].sort() }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/vapi/settings", { days: cfg.days, start: cfg.start, end: cfg.end });
      setCfg(data); toast.success("Horaires de Sara Dupont enregistrés");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="nicole-settings" className="rounded-2xl border border-[#E0E7FF] bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold text-[#1A1A1A] inline-flex items-center gap-1.5"><Settings2 className="w-4 h-4 text-[#4F46E5]" /> Paramètres Sara Dupont — horaires d'appels</div>
        <span data-testid="nicole-open-now" className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${cfg.open_now ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
          {cfg.open_now ? "Appels autorisés maintenant" : "Hors horaires"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DAYS.map((l, d) => (
          <button key={d} data-testid={`nicole-day-${d}`} onClick={() => toggle(d)} aria-pressed={cfg.days.includes(d)}
            className={`h-9 w-12 rounded-lg text-xs font-bold transition-colors ${cfg.days.includes(d) ? "bg-[#4F46E5] text-white" : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-[#3B423E]">Début
          <input data-testid="nicole-start" type="time" value={cfg.start} onChange={(e) => setCfg({ ...cfg, start: e.target.value })} className="block mt-1 h-9 px-2 rounded-lg border border-[#E2DFD8]" />
        </label>
        <label className="text-xs font-semibold text-[#3B423E]">Fin
          <input data-testid="nicole-end" type="time" value={cfg.end} onChange={(e) => setCfg({ ...cfg, end: e.target.value })} className="block mt-1 h-9 px-2 rounded-lg border border-[#E2DFD8]" />
        </label>
        <span className="text-xs text-[#75807A] pb-2">Fuseau : Europe/Brussels</span>
        <button data-testid="nicole-settings-save" onClick={save} disabled={saving}
          className="h-9 px-4 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors ml-auto">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
        </button>
      </div>
      <p data-testid="nicole-hours-label" className="text-[11px] text-[#75807A]">Sara Dupont n'appelle que : {cfg.label}. Les appels en série, rappels et relances prévus hors horaires attendent le prochain créneau ; l'appel manuel est bloqué.</p>
    </div>
  );
}

function NicoleStats() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/vapi/stats", { params: { weeks: 8 } }).then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  const t = d.totals;
  const max = Math.max(1, ...d.weeks.map((w) => w.calls));
  const kpis = [["Appels", t.calls, "#4F46E5"], ["Décrochés", t.answered, "#0EA5E9"], ["Intéressés", t.interested, "#166534"], ["Taux d'intérêt", `${t.interested_rate} %`, "#166534"], ["À rappeler", t.callback, "#1D4ED8"], ["Signés", t.signed, "#065f46"]];
  return (
    <div data-testid="nicole-stats" className="rounded-2xl border border-[#E0E7FF] bg-white p-4 space-y-4">
      <div className="text-sm font-bold text-[#1A1A1A]">8 dernières semaines</div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {kpis.map(([l, v, c]) => (
          <div key={l} data-testid={`nicole-kpi-${l}`} className="rounded-xl bg-[#F8FAFF] p-2.5">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#75807A]">{l}</div>
            <div className="text-xl font-black" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="nicole-weeks-table">
          <thead><tr className="text-left text-[#75807A]"><th className="py-1.5 pr-2">Semaine</th><th className="pr-2 w-1/3">Appels</th><th className="pr-2">Décrochés</th><th className="pr-2">Intéressés</th><th className="pr-2">Taux</th><th className="pr-2">À rappeler</th><th>Signés</th></tr></thead>
          <tbody>
            {d.weeks.map((w) => (
              <tr key={w.week_start} className="border-t border-[#F0EBE1]">
                <td className="py-1.5 pr-2 font-semibold whitespace-nowrap">{w.label}</td>
                <td className="pr-2"><div className="flex items-center gap-2"><div className="h-2 rounded-full bg-[#4F46E5]" style={{ width: `${(w.calls / max) * 100}%`, minWidth: w.calls ? 6 : 0 }} /><span>{w.calls}</span></div></td>
                <td className="pr-2">{w.answered}</td><td className="pr-2 font-bold text-[#166534]">{w.interested}</td>
                <td className="pr-2">{w.interested_rate} %</td><td className="pr-2">{w.callback}</td><td className="font-bold text-[#065f46]">{w.signed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#75807A]">Taux d'intérêt = intéressés ÷ appels décrochés. Sans réponse : jusqu'à {d.max_retries || "∞"} relance(s), tous les 2 jours.</p>
    </div>
  );
}

function NicoleScript() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("crm");
  useEffect(() => { if (open && !d) api.get("/admin/vapi/script").then((r) => setD(r.data)); }, [open, d]);
  const copy = () => { navigator.clipboard.writeText(d[lang]); toast.success("Script copié"); };
  return (
    <div className="rounded-2xl border border-[#E2DFD8] bg-white p-4">
      <button data-testid="nicole-script-toggle" onClick={() => setOpen((o) => !o)} className="text-sm font-bold text-[#4F46E5] inline-flex items-center gap-1.5"><FileText className="w-4 h-4" /> {open ? "Masquer" : "Voir"} le script de Sara Dupont</button>
      {open && d && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {[["crm", "Section CRM installée"], ["fr", "Script complet FR"], ["nl", "Script complet NL"]].map(([l, t]) => <button key={l} data-testid={`nicole-script-${l}`} onClick={() => setLang(l)} className={`h-8 px-3 rounded-lg text-xs font-bold ${lang === l ? "bg-[#4F46E5] text-white" : "bg-[#EEF2FF] text-[#4338CA]"}`}>{t}</button>)}
            <button data-testid="nicole-script-copy" onClick={copy} className="h-8 px-3 rounded-lg text-xs font-bold bg-white border border-[#E2DFD8] inline-flex items-center gap-1 ml-auto"><Copy className="w-3.5 h-3.5" /> Copier</button>
          </div>
          <pre data-testid="nicole-script-text" className="text-xs whitespace-pre-wrap bg-[#F8FAFF] rounded-lg p-3 max-h-96 overflow-auto text-[#1A1A1A]">{d[lang]}</pre>
        </div>
      )}
    </div>
  );
}

export default function NicoleCallsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, q] = await Promise.all([api.get("/admin/vapi/calls", { params: { limit: 100 } }), api.get("/admin/vapi/queue")]);
      setData(c.data); setQueue(q.data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="nicole-calls-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head font-black text-xl text-[#1A1A1A] flex items-center gap-2"><PhoneCall className="w-5 h-5 text-[#4F46E5]" /> Appels Sara Dupont</h2>
          <p className="text-xs text-[#75807A]">{data.total} appel(s) · {queue.length} en file d'attente</p>
        </div>
        <button data-testid="nicole-calls-refresh" onClick={load} className="h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#4F46E5] text-xs font-semibold inline-flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>
      <NicoleSettings />
      <CampaignsPanel />
      <NicoleStats />
      <NicoleScript />
      {queue.length > 0 && (
        <div data-testid="nicole-queue" className="rounded-xl bg-[#EEF2FF] p-3 text-xs text-[#3730A3] space-y-1">
          <div className="font-bold">File d'attente</div>
          {queue.map((q) => (
            <div key={q.id}>{{ callback: "Rappel planifié", retry: "Relance sans réponse" }[q.reason] || "Série"} · {q.status === "calling" ? "en cours" : `prévu ${fmtAt(q.run_after)}`}</div>
          ))}
        </div>
      )}
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>
        : data.items.length === 0 ? <div data-testid="nicole-calls-empty" className="rounded-2xl bg-white border border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">Aucun appel Sara Dupont pour l'instant.</div>
        : <div className="space-y-2">{data.items.map((c) => <NicoleCallRow key={c.id} c={c} showName onDeleted={(id) => setData((d) => ({ items: d.items.filter((x) => x.id !== id), total: d.total - 1 }))} />)}</div>}
    </section>
  );
}
